/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import dayjs from "dayjs";
import {DatePicker, Form, FormInstance, InputNumber, InputNumberProps, Radio} from "antd";
import {WarningOutlined} from "@ant-design/icons";
import styles from './BaechaWorkDayPopup.module.scss';
import {CarType} from "../../../types/Type";
import {BaechaWorkDay} from "../../../types/BaechaWorkDay";
import {Baecha} from "../../../types/Baecha";
import {SelectedDataIF} from "../../../types/SelectedDataIF";
import GroupAPI from "../../../api/groupAPI";

const getLabel = (type: string, priceStr: string) => (
	<div style={{ textAlign: 'center', height: '80px' }}>
		<div>{type}</div>
		<div>{priceStr}</div>
	</div>
);

interface AddBaechaWorkDayProps {
	form: FormInstance;
	selectedData: SelectedDataIF;
	selectedBaechaWorkDayData: BaechaWorkDay | undefined;
}

const BaechaWorkDayPopup = ({ form, selectedData, selectedBaechaWorkDayData }: AddBaechaWorkDayProps) => {
	const [priceData, setPriceData] = useState<any[]>([]);
	const [isEnableOldPrice, setIsEnableOldPrice] = useState(false);
	const [isEnableMidPrice, setIsEnableMidPrice] = useState(false);

	const isSmallCar = selectedData.baechaData?.carType
		&& (['1t', '2t', '3t', '1-2t', '1-3t', '2-3t', '5t'] as CarType[]).includes(selectedData.baechaData.carType);
	const isBigCar =  selectedData.baechaData?.carType
		&& (['54m', '58m', '75m', '5t_over', '54m_over', '58m_over', '28m', '40m', '60m', '70m'] as CarType[]).includes(selectedData.baechaData.carType);

	const getInitPrice = useCallback(() => {
		if (!selectedBaechaWorkDayData || !selectedBaechaWorkDayData.price) return 0;
		if (isSmallCar) return selectedBaechaWorkDayData.price;
		if (isBigCar) return selectedBaechaWorkDayData.price / 10000;
		return 0;
	}, [selectedBaechaWorkDayData, isSmallCar, isBigCar]);

	const getInitPriceForAddedItem = useCallback(() => {
		if (isSmallCar) {
			const target = priceData.find((item) => item.mhType === '1d');
			const priceType = selectedData.baechaData?.priceType;
			if (priceType === 'old') return target.oldPrice ? target.oldPrice : 0;
			if (priceType === 'mid') return target.midPrice ? target.midPrice : 0;
			if (priceType === 'new') return target.newPrice ? target.newPrice : 0;
			return 0;
		}
		if (isBigCar) {
			return 0;
		}
		return 0;
	}, [isSmallCar, isBigCar, priceData, selectedData.baechaData?.priceType]);

	const getMhTypeOptions = () => {
		if (!selectedData.baechaData?.carType) return [];

		const { carType } = selectedData.baechaData as Baecha;
		const oneHour = { label: '1시간', value: '1h' };
		const twoHour = { label: '2시간', value: '2h' };
		const halfDay = { label: '반나절', value: '4h' };
		const oneDay = { label: '하루', value: '1d' };

		if ((['54m', '58m', '75m', '28m', '40m'] as CarType[]).includes(carType)) return [ halfDay, oneDay ];
		if ((['60m', '70m'] as CarType[]).includes(carType)) return [ oneDay ];
		return [ oneHour, twoHour, halfDay, oneDay ];
	};

	const formatter: InputNumberProps<number>['formatter'] = (value) => {
		const [start, end] = `${value}`.split('.') || [];
		const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return `${end ? `${v}.${end}` : `${v}`}`;
	};

	const makePriceOptions = () => {
		const formMhType = form.getFieldValue('mhType');
		const target = priceData.find((item) => item.mhType === formMhType);

		if (!target) return [];

		const { oldPrice, midPrice, newPrice } = target;

		// 구, 중, 신 순서로 보여줘야 돼서 조금 복잡해짐.
		let result = [{ label: getLabel('신단가', `${newPrice.toString().slice(0, 2)}만 원`), value: newPrice }];

		if (isEnableMidPrice) {
			result = [{ label: getLabel('중단가', `${midPrice.toString().slice(0, 2)}만 원`), value: midPrice }, ...result];
		}
		if (isEnableOldPrice) {
			result = [{ label: getLabel('구단가', `${oldPrice.toString().slice(0, 2)}만 원`), value: oldPrice }, ...result];
		}

		return result;
	};

	const onChangeMhType = () => {
		const priceOptions = makePriceOptions()
		const targetValue = priceOptions[priceOptions.length - 1].value / 10000;
		form.setFieldValue('price', targetValue);
	};

	const convertCarTypeIntoNum = (carType: CarType): number => {
		if (carType === '1t') return 1;
		if (carType === '2t') return 2;
		if (carType === '3t') return 3;
		if (carType === '5t') return 5;
		if (carType === '54m') return 54;
		if (carType === '58m') return 58;
		if (carType === '75m') return 75;
		// 굴절
		if (carType === '28m') return 28;
		if (carType === '40m') return 40;
		if (carType === '60m') return 60;
		if (carType === '70m') return 70;
		return 0;
	}

  const getIsCanCallPass = (baechaCarType: CarType, userCarType: CarType) => {
		const userCarTypeNum = convertCarTypeIntoNum(userCarType);

		if (baechaCarType === '1t') return userCarTypeNum <= 3; // 1톤 발주는 1, 2, 3톤만 지원 가능
		if (baechaCarType === '2t') return userCarTypeNum === 2 || userCarTypeNum === 3; // 2톤 발주는 2, 3톤 지원 가능
		if (baechaCarType === '3t') return userCarTypeNum === 3 || userCarTypeNum === 5; // 3톤 발주는 3, 5톤만 지원 가능
		if (baechaCarType === '1-2t') return userCarTypeNum <= 2; // 1-2톤 발주는 1, 2톤만 지원 가능
		if (baechaCarType === '2-3t') return userCarTypeNum === 2 || userCarTypeNum === 3; // 2-3톤 발주는 2, 3톤만 지원 가능
		if (baechaCarType === '1-3t') return userCarTypeNum <= 3; // 1-3톤 발주는 1, 2, 3톤만 지원 가능
		if (baechaCarType === '5t') return userCarTypeNum >= 5; // 5톤 발주는 5톤, 54M, 58M, 75M 지원 가능
		if (baechaCarType === '54m') return userCarTypeNum >= 54; // 54M 발주는 54M, 58M, 75M 지원 가능
		if (baechaCarType === '58m') return userCarTypeNum >= 58; // 58M 발주는 58M, 75M 지원 가능
		if (baechaCarType === '75m') return userCarTypeNum >= 75; // 75M 발주는 75M 지원 가능
		if (baechaCarType === '54m_over') return userCarTypeNum >= 54;
		if (baechaCarType === '58m_over') return userCarTypeNum >= 58;
		// 굴절: 무조건 동종 차량만 지원 가능
		if (baechaCarType === '28m') return userCarTypeNum === 28;
		if (baechaCarType === '40m') return userCarTypeNum === 40;
		if (baechaCarType === '60m') return userCarTypeNum === 60;
		if (baechaCarType === '70m') return userCarTypeNum === 70;
		return false;
	}

	// configPrice 가져오기
	useEffect(() => {
		if (!selectedData?.baechaData?.baljuingGroupId) return;

		GroupAPI.getGroupConfigs(selectedData?.baechaData?.baljuingGroupId, ['price'])
			.then((res) => {
				setIsEnableOldPrice(res.config.enable_old_price);
				setIsEnableMidPrice(res.config.enable_mid_price);
				if (!selectedData.baechaData?.carType) return;
				const priceData = res.price.filter((item: any) => {
					return getIsCanCallPass(item.carType, selectedData.baechaData?.carType!)
				});
				setPriceData(priceData);
			})
			.catch((err) => console.error(err));
	}, [selectedData.baechaData?.baljuingGroupId, selectedData.baechaData?.carType]);

	// populating the form
	useEffect(() => {
		if (priceData.length > 0 ) {
			if (selectedBaechaWorkDayData) {
				// 기존 작업일 수정 시
				form.setFieldsValue({
					workDate: dayjs(selectedBaechaWorkDayData.workDate),
					mhType: selectedBaechaWorkDayData.mhType,
					price: getInitPrice(),
					overtimePrice: selectedBaechaWorkDayData.overtimePrice
						? selectedBaechaWorkDayData.overtimePrice / 10000
						: 0,
				});
			} else {
				// 새 작업일 추가 시
				form.setFieldsValue({
					workDate: dayjs(),
					mhType: '1d',
					price: getInitPriceForAddedItem(),
					overtimePrice: 0
				});
			}
		}

	}, [form, priceData, selectedBaechaWorkDayData, getInitPrice, getInitPriceForAddedItem]);

	return (
		<Form
			labelCol={{ xs: { span: 24 }, sm: { span: 4 } }}
			wrapperCol={{ xs: { span: 24 }, sm: { span: 18 } }}
			form={form}
			variant="outlined"
			size="large"
			style={{ width: '100%', marginTop: '20px' }}
		>
			{(selectedBaechaWorkDayData?.doneDate || selectedBaechaWorkDayData?.doneMemberId)
				&& (
					<div
						style={{ width: '100%', textAlign: 'center', color: 'red', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
						<WarningOutlined /> 이미 완료 처리 된 작업일정입니다.
					</div>
				)}
			<Form.Item label="작업 날짜" name="workDate">
				<DatePicker allowClear={false} />
			</Form.Item>
			<Form.Item label="작업 시간" name="mhType">
				<Radio.Group
					block
					options={getMhTypeOptions()}
					optionType="button"
					buttonStyle="solid"
					onChange={onChangeMhType}
				/>
			</Form.Item>
			<Form.Item label="단가" name="price">
				{isSmallCar
					&& (
						<Radio.Group
							className={styles.priceRadioGroup}
							block
							options={makePriceOptions()}
							optionType="button"
							buttonStyle="solid"
						/>
					)}
				{isBigCar
					&& (
						<InputNumber
							step={5}
							suffix="만 원"
							style={{ width: '100%', textAlign: 'right' }}
							formatter={formatter}
						/>
					)}
			</Form.Item>
			<Form.Item label="야간 연장" name="overtimePrice">
				<InputNumber suffix="만 원" style={{ width: '100%', textAlign: 'right' }} />
			</Form.Item>
		</Form>
	);
};

export default BaechaWorkDayPopup;